import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { HiOutlineRefresh, HiOutlineX, HiPaperAirplane } from "react-icons/hi";
import { HiOutlineHandThumbUp } from "react-icons/hi2";
import { HiOutlineHandThumbDown } from "react-icons/hi2";
import { HiOutlineSpeakerWave } from "react-icons/hi2";
import { ArrowUp } from "lucide-react";

const LOCAL_STORAGE_KEY = "chatHistory";

function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const [lastPosition, setLastPosition] = useState({
    latitude: 0,
    longitude: 0,
  });
  const [address, setAddress] = useState(null);
  const [settings, setSettings] = useState({
    title: "",
    logoImage: "",
    initialMessage: "",
    chatBoxColor: "#ffffff",
    userTextColor: "#000000",
  });

  const [reactions, setReactions] = useState(() => {
    // Load saved reactions from local storage
    const storedReactions = localStorage.getItem("reactions");
    return storedReactions ? JSON.parse(storedReactions) : {};
  });

  useEffect(() => {
    // Save reactions to local storage whenever they change
    localStorage.setItem("reactions", JSON.stringify(reactions));
  }, [reactions]);

  const [suggestedMessages, setSuggestedMessages] = useState([]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/settings`
      );
      setSettings(response.data);

      const initialMessage =
        response.data.initialMessage ||
        "Welcome to Bhutan Development Bank. How can I help you?";

      setMessages([{ text: initialMessage, user: false }]);
    } catch (error) {
      console.error("Error fetching settings:", error);
      setMessages([
        {
          text: "Welcome to Bhutan Development Bank. How can I help you?",
          user: false,
        },
      ]);
    }
  };

  const callLocationSaveApi = async (address) => {
    console.log("callLocationSaveApi", address);
    let location;
    if (!address.city) {
      location = { city: address.state_district, country: address.country };
    } else if (address) {
      location = { city: address.city, country: address.country };
    } else {
      console.log("No location to save");
      return;
    }
    console.log("location", location);
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/location`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: localStorage.getItem("userId"),
        location: address,
      }),
    });
    if (!res.ok) {
      console.log("Error in saving location");
    }
    console.log("Location saved");
  };
  const getLocation = async () => {
    const response = await fetch(
      `https://www.googleapis.com/geolocation/v1/geolocate?key=AIzaSyCtZ2xCzCUs53PDGyaMhVXPcySU9R2Lyj0`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) {
      throw new Error("Something went wrong");
    }
    const data = await response.json();

    //   const location = response.data.location;
    console.log("Location", data);

    const latitude = data.location.lat;
    const longitude = data.location.lng;
    lastPosition.latitude = latitude;
    lastPosition.longitude = longitude;
    setLastPosition(lastPosition);
    reverseGeoCode(latitude, longitude)
      .then((add) => {
        setAddress(add);
        console.log("address", add);
        callLocationSaveApi(add);
      })
      .catch((e) => {
        console.error(e.message);
      });
  };

  const reverseGeoCode = async (lat, lng) => {
    return fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=10&accept-language=en`
    )
      .then((response) => response.json())
      .then((data) => {
        const address = data.address;
        address.latitude = lat;
        address.longitude = lng;
        console.log("after reverse geocode", data);
        return address;
      })
      .catch((e) => {
        //console.error(e.message)
      });
  };
  useEffect(() => {
    const initializeChat = async () => {
      await fetchSettings(); // Fetch settings when the component mounts

      const storedUserId = localStorage.getItem("userId");
      // if (storedUserId) {
      //   console.log("hi, 1");
      // }
    };

    initializeChat();
  }, []);

  useEffect(() => {
    const storedMessages = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (storedMessages) {
      setMessages(JSON.parse(storedMessages));
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/settings`) // Replace with your actual API URL
      .then((response) => {
        console.log(response.data); // Log the API response to check if data is coming
        const { suggestedMessages } = response.data;
        setSuggestedMessages(suggestedMessages || []); // Set the state with suggested messages
      })
      .catch((error) => {
        console.error("Error fetching settings:", error);
      });
  }, []);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const sendMessage = async () => {
    console.log("sending message to server");
    if (input.trim() === "") return;

    const userMessage = { text: input, user: true };
    const conversationKey = "conversation"; // LocalStorage key for messages
    const conversationIdKey = "conversationId";

    // Add user message to the chat and localStorage
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages, userMessage];
      localStorage.setItem(conversationKey, JSON.stringify(updatedMessages)); // Save messages to localStorage
      return updatedMessages;
    });
    setInput(""); // Clear input field
    setIsLoading(true); // Show loading indicator

    try {
      // Retrieve existing messages from localStorage for context
      const storedMessages =
        JSON.parse(localStorage.getItem(conversationKey)) || [];
      let conversationId = localStorage.getItem(conversationIdKey);
      const userId = localStorage.getItem("userId");
      console.log("conversationId", conversationId);
      // Prepare conversation history to send to the API
      const conversationHistory = storedMessages.map((msg) => ({
        content: msg.text,
        role: msg.user ? "user" : "assistant",
      }));

      const eventSource = new EventSource(
        `${process.env.REACT_APP_BACKEND_URL}/chat/stream?conversationId=${
          conversationId || ""
        }&userId=${userId || ""}&message=${encodeURIComponent(
          input
        )}&chatbotId=yourChatbotId`
      );

      let botMessageText = ""; // Accumulate bot's response letter by letter

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setMessages((prevMessages) =>
            prevMessages.map((msg, index) =>
              index === prevMessages.length - 1 && !msg.user
                ? {
                    ...msg,
                    text: botMessageText,
                    _id: data.messageId || msg._id,
                  }
                : msg
            )
          );
          if (data.char) {
            botMessageText += data.char;
          }
          // console.log("bot messages", botMessageText)
        } catch (error) {
          console.error("Error parsing streamed data:", error);
        }
      };

      // Handle "end" event
      eventSource.addEventListener("end", (event) => {
        try {
          const endData = JSON.parse(event.data); // Parse end event data
          const { conversationId: newConversationId, userId } = endData;

          if (!conversationId && newConversationId) {
            localStorage.setItem("conversationId", newConversationId);
          }
          if (userId) {
            localStorage.setItem("userId", userId);
            console.log("server is returning userid ", userId);
            getLocation();
          }
          eventSource.close(); // Close the connection
          setIsLoading(false); // Hide loading indicator
        } catch (error) {
          console.error("Error parsing 'end' event data:", error);
        }
      });

      eventSource.onopen = () => {
        // Add a placeholder for bot message while streaming
        const botPlaceholder = { text: "", user: false };
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages, botPlaceholder];
          localStorage.setItem(
            conversationKey,
            JSON.stringify(updatedMessages)
          ); // Save updated messages to localStorage
          return updatedMessages;
        });
      };

      eventSource.onerror = () => {
        eventSource.close(); // Close the connection on error
        setIsLoading(false);
      };
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  // Clear conversation ID on page reload (new conversation will start)
  window.addEventListener("beforeunload", () => {
    localStorage.removeItem("conversationId"); // Clear conversation ID on page refresh
  });

  const handleRefresh = async () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    await fetchSettings(); // Fetch settings again on refresh
  };

  useEffect(() => {
    // Function to handle close button click
    const handleChatClose = () => {
      // Post a message to the parent window
      window.parent.postMessage("closeIframe", "*"); // You may want to use a more secure origin instead of "*"
    };

    // Add the event listener to the close button when the component mounts
    document
      .getElementById("close-button")
      .addEventListener("click", handleChatClose);

    // Remove the event listener when the component unmounts
    return () => {
      document
        .getElementById("close-button")
        .removeEventListener("click", handleChatClose);
    };
  }, []);

  const MarkdownComponents = {
    p: ({ children }) => <p className="">{children}</p>,
    h1: ({ children }) => <h1 className="text-2xl font-bold">{children}</h1>,
    h2: ({ children }) => <h2 className="text-xl font-bold">{children}</h2>,
    h3: ({ children }) => <h3 className="text-lg font-bold">{children}</h3>,
    ul: ({ children }) => <ul className="list-disc pl-4">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-4">{children}</ol>,
    li: ({ children }) => <li className="mb-1">{children}</li>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2">
        {children}
      </blockquote>
    ),
    code: ({ inline, children }) =>
      inline ? (
        <code className="bg-gray-1s00 rounded px-1 py-0.5">{children}</code>
      ) : (
        <pre className="bg-gray-100 rounded p-2 overflow-x-auto">
          <code>{children}</code>
        </pre>
      ),
    table: ({ children }) => (
      <div className="overflow-x-auto">
        <table className="border-collapse border border-gray-300 my-4 w-full">
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th className="border border-gray-300 px-4 py-2 bg-gray-100">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-gray-300 px-4 py-2">{children}</td>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-bold text-blue-600 hover:underline"
      >
        {children}
      </a>
    ),
  };

  const renderMessage = (message) => {
    const textContent = typeof message.text === "string" ? message.text : "";
    const isUserMessage = message.user;

    return (
      <div
        className={`flex w-fit max-w-full rounded-[1.4rem] py px-5 py-4 text-sm ${
          isUserMessage ? "ml-auto" : "mr-auto"
        }`}
        style={{
          backgroundColor: isUserMessage ? settings.chatBoxColor : "#f4f4f5",
          color: isUserMessage ? settings.userTextColor : "#111827",
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={MarkdownComponents}
          className="markdown-body"
        >
          {textContent}
        </ReactMarkdown>
      </div>
    );
  };

  // Reaction handler with dynamic backend URL
  const handleReaction = async (messageId, reaction) => {
    try {
      if (!messageId) {
        console.error("❌ Error: messageId is undefined!");
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      console.log(
        `✅ Sending reaction: ${reaction} for messageId: ${messageId}`
      );

      // Get the current reaction state
      const currentReaction = reactions[messageId];
      const newReaction = currentReaction === reaction ? null : reaction; // Remove reaction if clicked again

      // Send the updated reaction to the backend
      const response = await fetch(`${backendUrl}/api/reactions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messageId, reaction: newReaction }),
      });

      const data = await response.json();
      console.log(`${reaction} saved:`, data);

      // Update the state and persist in local storage
      setReactions((prevReactions) => {
        const updatedReactions = { ...prevReactions, [messageId]: newReaction };
        localStorage.setItem("reactions", JSON.stringify(updatedReactions));
        return updatedReactions;
      });
    } catch (error) {
      console.error("❌ Error saving reaction:", error);
    }
  };

  const speakText = (text) => {
    if (!text) return;

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    window.speechSynthesis.speak(utterance);
  };

  return (
    <div className="flex w-full h-full items-center justify-center">
      <div className="flex flex-col max-w-2xl w-full max-h-[1080px] h-screen bg-white rounded-2xl border overflow-hidden">
        {/* Header with Refresh and Close Buttons */}
        <div className="flex justify-between items-center text-black/80 py-3 px-5 rounded-t-xl border-b">
          <div className="flex items-center">
            {/* Logo */}
            {settings.isLoading ? (
              <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse mr-2"></div>
            ) : (
              settings.logoImage && (
                <img
                  src={settings.logoImage}
                  alt="Logo"
                  className="w-8 h-8 rounded-full mr-2"
                />
              )
            )}
            {settings.isLoading ? (
              <div className="h-6 w-24 bg-gray-200 animate-pulse"></div>
            ) : (
              <h1 className="text-[14px] md:text-[20px] font-semibold">
                {settings.title || "Bhutan Development Bank"}
              </h1>
            )}
          </div>
          <div className="flex items-center">
            <button
              onClick={handleRefresh}
              className="w-8 h-8 flex justify-center items-center bg-zinc-50 border border-zinc-100 hover:bg-zinc-100 rounded-full text-zinc-500 hover:text-zinc-700 transition mr-2"
            >
              <HiOutlineRefresh className="w-4 h-4" />
            </button>
            <button
              id="close-button"
              className="w-8 h-8 flex justify-center items-center bg-zinc-50 border border-zinc-100 hover:bg-zinc-100 rounded-full text-zinc-500 hover:text-zinc-700 transition"
            >
              <HiOutlineX className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-grow overflow-auto space-y-4 p-4 chat_NoMindBhutan">
          {messages.map((message, index) => (
            <React.Fragment key={index}>
              <div className="relative pt-4">
                {renderMessage(message, MarkdownComponents)}

                {/* Thumbs-up and thumbs-down for bot messages, only after the first bot message */}
                {!message.user && index > 0 && (
                  <div className="flex items-center space-x-3 text-gray-500 px-2 mt-2">
                    <button
                      onClick={() => handleReaction(message._id, "thumbsUp")}
                      style={{
                        color:
                          reactions[message._id] === "thumbsUp"
                            ? "gray"
                            : "black",
                      }}
                    >
                      <HiOutlineHandThumbUp className="hover:text-gray-700 transition" />
                    </button>

                    <button
                      onClick={() => handleReaction(message._id, "thumbsDown")}
                      style={{
                        color:
                          reactions[message._id] === "thumbsDown"
                            ? "gray"
                            : "black",
                      }}
                    >
                      <HiOutlineHandThumbDown className="hover:text-gray-700 transition" />
                    </button>

                    <button
                      onClick={() => speakText(message.text)}
                      title="Read aloud"
                    >
                      <HiOutlineSpeakerWave className="hover:text-gray-700 transition" />
                    </button>
                  </div>
                )}
              </div>

              {/* Show loading indicator after the last user message */}
              {isLoading && index === messages.length - 1 && message.user && (
                <div className="w-fit mr-auto my-1 px-5 py-4 rounded-[1.4rem] border bg-zinc-50 text-black/80">
                  <div className="flex items-center space-x-3">
                    <div className="flex space-x-1 mt-2">
                      <div
                        className="animate-bounce h-[6px] w-[6px] bg-black/50 rounded-full"
                        style={{ animationDelay: "0ms" }}
                      ></div>
                      <div
                        className="animate-bounce h-[6px] w-[6px] bg-black/50 rounded-full"
                        style={{ animationDelay: "200ms" }}
                      ></div>
                      <div
                        className="animate-bounce h-[6px] w-[6px] bg-black/50 rounded-full"
                        style={{ animationDelay: "400ms" }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions Area */}
        <div className="p-2 py-1">
          <div className="flex overflow-x-auto chat_NoMindBhutan">
            <div className="flex flex-nowrap gap-2 mb-1">
              {suggestedMessages.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => setInput(suggestion)}
                  className="bg-zinc-50 border border-zinc-100 text-zinc-600 rounded-full px-3 py-2 gap-2 text-sm whitespace-nowrap hover:bg-zinc-200 transition"
                >
                  {suggestion || "Fetching Data"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Input Area */}
        <div className="mb-4 mr-2 ml-2 mt-1 flex items-center">
          <div className="relative flex-grow flex items-center">
            <textarea
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = `${Math.min(
                  e.target.scrollHeight,
                  120
                )}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              className="w-full text-sm p-4 border border-zinc-300 rounded-[1.5rem] text-black/80 focus:outline-none focus:ring-1 focus:ring-black/75 pr-12 resize-none overflow-hidden max-h-[120px] scrollbar-hide"
              placeholder="Type a message..."
              rows={1}
              style={{
                minHeight: "52px",
                maxHeight: "120px",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            />
            <button
              onClick={sendMessage}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-zinc-200 bg-black rounded-full w-8 h-8 flex items-center justify-center hover:bg-zinc-600 hover:text-zinc-100 transition"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Powered by section */}
        <a
          href="https://www.nomindbhutan.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-center text-xs bg-zinc-50 hover:bg-zinc-100 transition py-3 text-zinc-500"
        >
          Powered by{" "}
          <div className="font-semibold inline-flex items-center text-zinc-600">
            NoMindBhutan
            <img
              src="/nomind.png"
              alt="NoMindBhutan Logo"
              className="ml-1 w-4 h-4"
            />
          </div>
        </a>
      </div>
    </div>
  );
}

export default App;
